import clsx from 'clsx';
import _floor from 'lodash/floor';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { FormRadioGroup } from '@/components/ui/radio-group';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';

import { BudgetCalculationSection } from './budget-calculation-section';
import { useChannelFormContext } from './channel-form-context';
import { OpeningBudget } from './opening-budget';

interface BudgetAllocationProps {
  channelSlug: string;
}

export function BudgetAllocation({ channelSlug }: BudgetAllocationProps) {
  const { validateChannelBudget } = useChannelFormContext();
  const { calculateTotalBudget } = useBudgetType();
  const { hookForm } = useJobPostingContext();
  const { control, watch, getValues } = hookForm;

  const openingBudgets = watch(`channels.${channelSlug}.openingBudgets`);
  const budgetAllocation = watch(`channels.${channelSlug}.budgetAllocation`);
  const channelBudget = watch(`channels.${channelSlug}.channelBudget`);
  const budgetType = watch(`channels.${channelSlug}.budgetType`);
  const startsAt = watch(`channels.${channelSlug}.startsAt`);
  const endAt = watch(`channels.${channelSlug}.endAt`);

  const hasMultipleOpenings = Object.keys(openingBudgets).length > 1;

  const perOpeningBudget = useMemo(() => {
    if (!channelBudget) return null;
    return _floor(channelBudget / Object.keys(openingBudgets).length, 2);
  }, [channelBudget, openingBudgets]);

  const budgetOptions = [
    {
      value: 'evenly',
      label: (
        <FormattedMessage
          defaultMessage="Allocate evenly across openings"
          description="Label for the evenly allocated budget option"
        />
      ),
      id: 'evenly-allocated',
    },
    {
      value: 'per-funnel',
      label: (
        <FormattedMessage
          defaultMessage="Allocate spend per opening"
          description="Label for the per-funnel allocated budget option"
        />
      ),
      id: 'per-funnel-allocated',
    },
  ];

  return (
    <>
      {hasMultipleOpenings && (
        <FormRadioGroup
          control={control}
          name={`channels.${channelSlug}.budgetAllocation`}
          options={budgetOptions}
          onChange={validateChannelBudget}
        />
      )}

      {perOpeningBudget !== null && budgetAllocation === 'evenly' && (
        <Typography
          variant="body1"
          className={clsx(
            '!text-fx-neutral-600 -mt-4',
            hasMultipleOpenings ? 'ml-6' : 'ml-0',
          )}
        >
          {hasMultipleOpenings ? (
            <FormattedMessage
              defaultMessage="Spend per opening ({count} openings): {perOpeningBudget}"
              values={{
                count: Object.keys(openingBudgets).length,
                perOpeningBudget: (
                  <span className="font-medium">${perOpeningBudget}</span>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Spend for {openingTitle}: {perOpeningBudget}"
              values={{
                openingTitle: (
                  <span className="italic">
                    {getValues(
                      `jobDetails.${Object.keys(openingBudgets)[0]}.title`,
                    )}
                  </span>
                ),
                perOpeningBudget: (
                  <span className="font-medium">${perOpeningBudget}</span>
                ),
              }}
            />
          )}
        </Typography>
      )}

      {budgetAllocation === 'per-funnel' && (
        <>
          <Typography
            variant="body1"
            className="!text-fx-neutral-600 -mt-4 ml-6"
          >
            <FormattedMessage
              defaultMessage="{count, plural, one {1 opening} other {{count} openings}}"
              values={{
                count: Object.keys(openingBudgets).length,
              }}
            />
          </Typography>
          <div className="bg-fx-neutral-50 flex flex-col gap-4 rounded p-4">
            {Object.keys(openingBudgets).map(openingId => {
              const openingBudget = watch(
                `channels.${channelSlug}.openingBudgets.${openingId}`,
              );

              return (
                <>
                  <OpeningBudget
                    key={openingId}
                    channelSlug={channelSlug}
                    openingId={openingId}
                    showDeleteButton={Object.keys(openingBudgets).length > 1}
                  />
                  {!(budgetType === 'lifetime' || !budgetType) && (
                    <>
                      <BudgetCalculationSection
                        channelSlug={channelSlug}
                        unitBudget={openingBudget}
                        totalBudget={calculateTotalBudget({
                          budgetType,
                          budgetPerUnit: openingBudget,
                          startDate: startsAt,
                          endDate: endAt,
                        })}
                        className="-mt-2"
                        isOpeningSection
                      />
                      <div className="bg-fx-neutral-200 h-px w-full" />
                    </>
                  )}
                </>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
